import React, { useState } from 'react';
import { Button } from './Button';
import FavoriteButton from './FavoriteButton';
import MicroFeedback from './MicroFeedback';
import { RefreshCwIcon, HeadphonesIcon, BookOpenIcon, BrainIcon, SparklesIcon, ArrowLeftIcon, ThumbsUpIcon, ThumbsDownIcon, InfoIcon, XIcon, BarChart2Icon } from 'lucide-react';
interface CoachingSuggestionProps {
  mood?: any;
  onExploreThemes: () => void;
  onBackToHome: () => void;
  onStartExercise: (exerciseId: string) => void;
}
const CoachingSuggestion: React.FC<CoachingSuggestionProps> = ({
  mood,
  onExploreThemes,
  onBackToHome,
  onStartExercise
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState<string>('default');
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [showInsightPanel, setShowInsightPanel] = useState(false);
  // Generate suggestions based on mood
  const getSuggestions = () => {
    if (!mood) return defaultSuggestions;
    // Determine mood type based on x and y coordinates
    const {
      x,
      y
    } = mood;
    if (y < 30 && x > 70) {
      return energeticPositiveSuggestions;
    } else if (y < 30 && x < 30) {
      return energeticNegativeSuggestions;
    } else if (y > 70 && x < 30) {
      return calmNegativeSuggestions;
    } else if (y > 70 && x > 70) {
      return calmPositiveSuggestions;
    }
    return defaultSuggestions;
  };
  // Get mood description for insight panel
  const getMoodDescription = () => {
    if (!mood) return {
      energy: 'gemiddeld',
      positivity: 'neutraal'
    };
    const {
      x,
      y
    } = mood;
    let energy = 'gemiddeld';
    let positivity = 'neutraal';
    // Energy level (y-axis)
    if (y < 30) energy = 'hoog';else if (y > 70) energy = 'laag';
    // Positivity (x-axis)
    if (x < 30) positivity = 'negatief';else if (x > 70) positivity = 'positief';
    return {
      energy,
      positivity
    };
  };
  const handleSuggestionSelect = (exerciseId: string) => {
    setShowFeedback(true);
    onStartExercise(exerciseId);
  };
  const handleRefreshSuggestions = () => {
    // Rotate through different suggestion sets
    if (currentSuggestions === 'default') {
      setCurrentSuggestions('alternative');
    } else {
      setCurrentSuggestions('default');
    }
  };
  const handleFeedback = (isPositive: boolean) => {
    setFeedbackGiven(true);
    // In a real app, this would send feedback to the backend
  };
  const toggleInsightPanel = () => {
    setShowInsightPanel(!showInsightPanel);
  };
  // Determine which suggestion set to use
  const suggestions = getSuggestions()[currentSuggestions === 'default' ? 0 : 1];
  const moodDescription = getMoodDescription();
  return <div className="w-full max-w-xl mx-auto py-8" data-id="element-216">
      <div className="flex items-center mb-8" data-id="element-217">
        <button onClick={onBackToHome} className="mr-4 p-2 rounded-full hover:bg-gray-100" aria-label="Terug naar home" data-id="element-218">
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-219" />
        </button>
        <h1 className="text-2xl font-light text-gray-800" data-id="element-220">
          Coaching suggesties
        </h1>
      </div>

      {/* Insight panel button */}
      <div className="mb-6 flex justify-end" data-id="element-221">
        <button onClick={toggleInsightPanel} className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors" data-id="element-222">
          <InfoIcon className="w-4 h-4 mr-1" data-id="element-223" />
          <span data-id="element-224">Hoe werken suggesties?</span>
        </button>
      </div>

      {/* Insight panel explaining how suggestions work */}
      {showInsightPanel && <div className="mb-8 p-5 border-2 border-gray-300 rounded-xl bg-gray-50 relative" data-id="element-225">
          <button onClick={toggleInsightPanel} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200" aria-label="Sluit uitleg" data-id="element-226">
            <XIcon className="w-4 h-4 text-gray-600" data-id="element-227" />
          </button>
          <div className="flex items-start mb-4" data-id="element-228">
            <BarChart2Icon className="w-5 h-5 text-gray-700 mr-3 mt-0.5 flex-shrink-0" data-id="element-229" />
            <div data-id="element-230">
              <h3 className="text-base font-medium text-gray-800 mb-2" data-id="element-231">
                Hoe we je suggesties personaliseren
              </h3>
              <p className="text-sm text-gray-600 mb-3" data-id="element-232">
                Op basis van je check-in data stellen we oefeningen voor die het
                beste passen bij je huidige toestand.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mb-4" data-id="element-233">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-234">
              Je huidige stemming:
            </h4>
            <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-235">
              <div className="p-3 border border-gray-200 rounded-lg bg-white" data-id="element-236">
                <p className="text-xs text-gray-500 mb-1" data-id="element-237">Energieniveau</p>
                <p className="text-sm font-medium text-gray-800" data-id="element-238">
                  {moodDescription.energy}
                </p>
              </div>
              <div className="p-3 border border-gray-200 rounded-lg bg-white" data-id="element-239">
                <p className="text-xs text-gray-500 mb-1" data-id="element-240">Stemming</p>
                <p className="text-sm font-medium text-gray-800" data-id="element-241">
                  {moodDescription.positivity}
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4" data-id="element-242">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-243">
              Daarom stellen we voor:
            </h4>
            <ul className="text-sm text-gray-600 space-y-2 pl-5 list-disc" data-id="element-244">
              {moodDescription.energy === 'hoog' && moodDescription.positivity === 'positief' && <>
                    <li data-id="element-245">Oefeningen om je energie te kanaliseren</li>
                    <li data-id="element-246">Mindfulness om je positieve stemming te behouden</li>
                  </>}
              {moodDescription.energy === 'hoog' && moodDescription.positivity === 'negatief' && <>
                    <li data-id="element-247">Kalmeringstechnieken om spanning te verminderen</li>
                    <li data-id="element-248">Oefeningen om negatieve gedachten te verwerken</li>
                  </>}
              {moodDescription.energy === 'laag' && moodDescription.positivity === 'positief' && <>
                    <li data-id="element-249">
                      Meditatieve oefeningen die je rustige stemming
                      ondersteunen
                    </li>
                    <li data-id="element-250">Reflectie-oefeningen voor meer inzicht</li>
                  </>}
              {moodDescription.energy === 'laag' && moodDescription.positivity === 'negatief' && <>
                    <li data-id="element-251">Oefeningen voor zelfcompassie en acceptatie</li>
                    <li data-id="element-252">Technieken om je stemming geleidelijk te verbeteren</li>
                  </>}
              {(moodDescription.energy === 'gemiddeld' || moodDescription.positivity === 'neutraal') && <>
                  <li data-id="element-253">Algemene oefeningen voor mentaal welzijn</li>
                  <li data-id="element-254">Technieken voor focus en mindfulness</li>
                </>}
            </ul>
          </div>
        </div>}

      <div className="mb-8 p-6 border-2 border-gray-300 rounded-xl bg-gray-50 flex items-start" data-id="element-255">
        <SparklesIcon className="w-6 h-6 text-gray-700 mr-4 flex-shrink-0 mt-0.5" data-id="element-256" />
        <div data-id="element-257">
          <p className="text-base text-gray-700 mb-2" data-id="element-258">
            {mood ? `Op basis van je ${mood.label} stemming stel ik dit voor...` : 'Hier zijn enkele suggesties voor je...'}
          </p>
          <p className="text-sm text-gray-500" data-id="element-259">
            Deze suggesties zijn afgestemd op jouw huidige energie en focus
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 mb-10" data-id="element-260">
        {suggestions.map(suggestion => <div key={suggestion.id} className="p-6 border-2 border-gray-300 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors duration-200 relative" onClick={() => handleSuggestionSelect(suggestion.id)} data-id="element-261">
            <div className="absolute top-4 right-4" data-id="element-262">
              <FavoriteButton size="sm" data-id="element-263" />
            </div>
            <div className="flex items-start" data-id="element-264">
              <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mr-4" data-id="element-265">
                {suggestion.icon}
              </div>
              <div data-id="element-266">
                <h3 className="text-lg font-medium text-gray-800 mb-1" data-id="element-267">
                  {suggestion.title}
                </h3>
                <p className="text-sm text-gray-600 mb-2" data-id="element-268">
                  {suggestion.duration}
                </p>
                <p className="text-sm text-gray-600" data-id="element-269">
                  {suggestion.description}
                </p>
                {suggestion.matchReason && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-270">
                    <p className="text-xs text-gray-500 flex items-center" data-id="element-271">
                      <InfoIcon className="w-3 h-3 mr-1" data-id="element-272" />
                      <span data-id="element-273">Aanbevolen omdat: {suggestion.matchReason}</span>
                    </p>
                  </div>}
              </div>
            </div>
          </div>)}
      </div>

      {!feedbackGiven && <div className="p-6 border-2 border-gray-300 rounded-xl mb-10" data-id="element-274">
          <p className="text-base text-gray-700 mb-4 text-center" data-id="element-275">
            Zijn deze suggesties nuttig voor je?
          </p>
          <div className="flex justify-center space-x-4" data-id="element-276">
            <Button variant="secondary" leftIcon={<ThumbsUpIcon className="w-4 h-4" data-id="element-278" />} onClick={() => handleFeedback(true)} data-id="element-277">
              Ja, nuttig
            </Button>
            <Button variant="secondary" leftIcon={<ThumbsDownIcon className="w-4 h-4" data-id="element-280" />} onClick={() => handleFeedback(false)} data-id="element-279">
              Niet echt
            </Button>
          </div>
        </div>}

      <div className="flex flex-col md:flex-row justify-between gap-4" data-id="element-281">
        <Button variant="secondary" leftIcon={<RefreshCwIcon className="w-4 h-4" data-id="element-283" />} onClick={handleRefreshSuggestions} data-id="element-282">
          Andere suggesties
        </Button>
        <Button onClick={onExploreThemes} data-id="element-284">Bekijk alle thema's</Button>
      </div>

      {showFeedback && <MicroFeedback message="Goed dat je voor jezelf kiest. Deze oefening helpt je te ontspannen." onClose={() => setShowFeedback(false)} data-id="element-285" />}
    </div>;
};
// Default suggestions
const defaultSuggestions = [[{
  id: 'stress-1',
  title: 'Mini-ademhalingsoefening',
  duration: '1 minuut',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-286" />,
  description: 'Korte ademhalingsoefening voor focus en kalmte',
  matchReason: 'Effectief voor algemene stress en focus'
}, {
  id: 'sleep-2',
  title: 'Ontspanningsmeditatie',
  duration: '5 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-287" />,
  description: 'Rustgevende meditatie voor ontspanning',
  matchReason: 'Helpt bij het tot rust komen'
}, {
  id: 'focus-1',
  title: 'Taakfocus',
  duration: '2 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-288" />,
  description: 'Korte oefening om je gedachten te ordenen',
  matchReason: 'Verbetert concentratie en prioriteiten stellen'
}], [{
  id: 'trauma-1',
  title: 'Veilige plek visualisatie',
  duration: '7 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-289" />,
  description: 'Creëer een mentale veilige plek voor rust en herstel',
  matchReason: 'Helpt bij het creëren van emotionele veiligheid'
}, {
  id: 'stress-2',
  title: 'Bodyscan',
  duration: '8 minuten',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-290" />,
  description: 'Scan je lichaam voor spanning en laat het los',
  matchReason: 'Vermindert lichamelijke spanning'
}]];
// Energetic and positive suggestions
const energeticPositiveSuggestions = [[{
  id: 'focus-1',
  title: 'Pomodoro focus',
  duration: '2 minuten',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-291" />,
  description: 'Kanaliseer je energie in productieve focus',
  matchReason: 'Past bij je hoge energieniveau en positieve stemming'
}, {
  id: 'stress-1',
  title: 'Mindful moment',
  duration: '3 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-292" />,
  description: 'Even stilstaan bij je positieve energie',
  matchReason: 'Helpt je positieve stemming vast te houden'
}], [{
  id: 'stress-3',
  title: 'Grenzen stellen',
  duration: '5 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-293" />,
  description: 'Leer je energie te beschermen en gericht in te zetten',
  matchReason: 'Effectief bij hoog energieniveau'
}, {
  id: 'focus-3',
  title: 'Taakanalyse',
  duration: '4 minuten',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-294" />,
  description: 'Structureer je energie in concrete stappen',
  matchReason: 'Helpt je energie productief te gebruiken'
}]];
// Energetic but negative suggestions
const energeticNegativeSuggestions = [[{
  id: 'stress-1',
  title: 'Ademhalingsoefening',
  duration: '3 minuten',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-295" />,
  description: 'Kalm je gedachten en verminder spanning',
  matchReason: 'Helpt bij het verminderen van negatieve spanning'
}, {
  id: 'stress-2',
  title: 'Bodyscan',
  duration: '8 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-296" />,
  description: 'Herken spanning in je lichaam en laat het los',
  matchReason: 'Effectief bij hoog energieniveau en negatieve gevoelens'
}], [{
  id: 'trauma-1',
  title: 'Veilige plek visualisatie',
  duration: '7 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-297" />,
  description: 'Creëer een mentale veilige plek voor rust',
  matchReason: 'Helpt bij het omgaan met negatieve emoties'
}, {
  id: 'rel-3',
  title: 'Communicatiestijlen',
  duration: '3 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-298" />,
  description: 'Begrijp hoe spanning je communicatie beïnvloedt',
  matchReason: 'Geeft inzicht in je communicatie bij stress'
}]];
// Calm but negative suggestions
const calmNegativeSuggestions = [[{
  id: 'rel-2',
  title: 'Empathie oefening',
  duration: '6 minuten',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-299" />,
  description: 'Verbind met positieve emoties en perspectieven',
  matchReason: 'Helpt bij het verbeteren van je stemming'
}, {
  id: 'trauma-3',
  title: 'Zelfcompassie',
  duration: '5 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-300" />,
  description: 'Leer met mildheid naar jezelf te kijken',
  matchReason: 'Effectief bij rustig maar negatief gevoel'
}], [{
  id: 'trauma-2',
  title: 'Schrijfoefening',
  duration: '10 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-301" />,
  description: 'Verwerk gedachten en gevoelens door te schrijven',
  matchReason: 'Past bij je rustige toestand en helpt bij verwerking'
}, {
  id: 'focus-4',
  title: 'Mindful focus',
  duration: '7 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-302" />,
  description: 'Richt je aandacht op het hier en nu',
  matchReason: 'Helpt bij het verschuiven van negatieve focus'
}]];
// Calm and positive suggestions
const calmPositiveSuggestions = [[{
  id: 'sleep-2',
  title: 'Ontspanningsmeditatie',
  duration: '12 minuten',
  icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-303" />,
  description: 'Verdiep je rustige stemming met deze meditatie',
  matchReason: 'Versterkt je huidige rustige, positieve toestand'
}, {
  id: 'rel-1',
  title: 'Actief luisteren',
  duration: '4 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-304" />,
  description: 'Verbeter je relaties vanuit een rustige basis',
  matchReason: 'Bouwt voort op je kalme, positieve stemming'
}], [{
  id: 'sleep-3',
  title: 'Slaaphygiëne tips',
  duration: '4 minuten',
  icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-305" />,
  description: 'Optimaliseer je rust voor betere slaap',
  matchReason: 'Past bij je ontspannen toestand'
}, {
  id: 'trauma-2',
  title: 'Reflectie schrijfoefening',
  duration: '10 minuten',
  icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-306" />,
  description: 'Reflecteer op wat je rust geeft',
  matchReason: 'Helpt je inzicht te krijgen in je positieve stemming'
}]];
export default CoachingSuggestion;